import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, ArrowRight, ShieldCheck, ClipboardList } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPECTATIONS = [
  { title: "What to expect", body: "A friendly welcome, a gentle examination, and clear communication throughout. If your child is nervous, we take extra time to build trust." },
  { title: "Consent requirements", body: "Written consent from a parent or guardian is required before any check-up or treatment. We provide the forms to your school." },
  { title: "How appointments work", body: "Appointments are short and structured. Your child is called from class, seen individually, then returned to class." },
  { title: "Preparing your child", body: "Talk about the visit positively at home. Remind them the team is there to help. There's no need to worry about brushing perfectly before the visit." },
  { title: "After the appointment", body: "You'll receive a written report with any recommendations. Feel free to contact us with any questions." },
  { title: "Privacy & clinical information", body: "We handle health information in line with Australian privacy laws. See our Privacy Policy for full details." },
];

const initialState = {
  parent_name: "",
  email: "",
  phone: "",
  child_school: "",
  child_age: "",
  message: "",
  consent: false,
};

const ForParents = () => {
  if (typeof document !== "undefined") document.title = "For Parents — School Smiles Australia";
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.parent_name.trim()) e.parent_name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.message.trim()) e.message = "Please add a short message";
    if (!form.consent) e.consent = "Consent is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return toast.error("Please check the highlighted fields.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries/parent`, form);
      toast.success("Thanks! We'll get back to you shortly.");
      setForm(initialState);
    } catch (_) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="container-page pt-16 md:pt-24 pb-8" data-testid="parents-hero">
        <div className="max-w-3xl">
          <span className="eyebrow"><HeartHandshake size={14} strokeWidth={2.5}/> For parents</span>
          <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#14324A]">
            Everything you need to know before your child's visit
          </h1>
          <p className="mt-5 text-[#4B6B86] text-lg leading-relaxed">
            We know entrusting your child's health to a new provider is a big step. Here's what we do, how consent works and how you'll hear from us.
          </p>
        </div>
      </section>

      <section className="container-page py-12" data-testid="parents-expectations">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {EXPECTATIONS.map((x, i) => (
            <div key={x.title} className="card-soft p-8" data-testid={`parents-expect-${i}`}>
              <h3 className="font-display font-semibold text-lg text-[#14324A]">{x.title}</h3>
              <p className="mt-3 text-sm text-[#4B6B86] leading-relaxed">{x.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page py-12 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="parents-costs">
        <div className="card-soft p-10">
          <h2 className="font-display font-bold text-2xl text-[#14324A]">Costs and payment</h2>
          <p className="mt-3 text-[#4B6B86] leading-relaxed">
            Fees vary depending on the services provided and your circumstances. We'll always explain costs in advance where possible. If you have questions about pricing, please get in touch before your child's visit.
          </p>
        </div>
        <div className="card-soft p-10 bg-[#F4F9FF]">
          <div className="flex items-center gap-2 text-[#1677C8] font-semibold">
            <ShieldCheck size={18} strokeWidth={2.5}/> Medicare & CDBS
          </div>
          <h2 className="mt-2 font-display font-bold text-2xl text-[#14324A]">Child Dental Benefits Schedule</h2>
          <p className="mt-3 text-[#4B6B86] leading-relaxed">
            Some children may be eligible for support under the Child Dental Benefits Schedule (CDBS). Eligibility rules and coverage amounts change from time to time. Please confirm your child's eligibility and coverage directly with Medicare, or ask our team — we'll do our best to help you check.
          </p>
        </div>
      </section>

      {/* Parent consent form promo card */}
      <section className="container-page py-6" data-testid="parents-consent-promo">
        <div className="card-soft p-8 md:p-10 bg-[#14324A] text-white flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-[#FFD45C] text-[#14324A] inline-flex items-center justify-center shrink-0">
            <ClipboardList size={26} strokeWidth={2.5}/>
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-[#FFD45C] font-semibold">Ready to go?</div>
            <h2 className="mt-1 font-display font-bold text-2xl md:text-3xl">Parent Consent &amp; Medical Information Form</h2>
            <p className="mt-2 text-white/75 leading-relaxed">
              Once you've read the information above, complete the consent and short medical history form for your child. Takes most families around 5 minutes.
            </p>
          </div>
          <Link to="/parent-consent" className="btn-accent shrink-0" data-testid="parents-consent-cta">
            Open the form
            <ArrowRight size={18} strokeWidth={2.5}/>
          </Link>
        </div>
      </section>

      {/* PARENT FORM */}
      <section className="container-page py-16" data-testid="parents-form-section">
        <div className="card-soft p-8 md:p-12">
          <SectionHeading eyebrow="Get in touch" title="Parent enquiry" description="Have a question? Send us a note and we'll respond within two working days."/>
          <form className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" onSubmit={onSubmit} noValidate data-testid="parent-enquiry-form">
            <FieldBlock label="Your name" error={errors.parent_name}>
              <Input value={form.parent_name} onChange={(e)=>setField("parent_name", e.target.value)} data-testid="input-parent-name"/>
            </FieldBlock>
            <FieldBlock label="Email" error={errors.email}>
              <Input type="email" value={form.email} onChange={(e)=>setField("email", e.target.value)} data-testid="input-parent-email"/>
            </FieldBlock>
            <FieldBlock label="Phone" error={errors.phone}>
              <Input value={form.phone} onChange={(e)=>setField("phone", e.target.value)} data-testid="input-parent-phone"/>
            </FieldBlock>
            <FieldBlock label="Child's school (optional)">
              <Input value={form.child_school} onChange={(e)=>setField("child_school", e.target.value)} data-testid="input-child-school"/>
            </FieldBlock>
            <FieldBlock label="Child's age (optional)">
              <Input value={form.child_age} onChange={(e)=>setField("child_age", e.target.value)} data-testid="input-child-age"/>
            </FieldBlock>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium text-[#14324A]">Message</Label>
              <Textarea rows={5} value={form.message} onChange={(e)=>setField("message", e.target.value)} data-testid="input-parent-message" placeholder="How can we help?" className="mt-2"/>
              {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
            </div>
            <div className="md:col-span-2 flex items-start gap-3">
              <Checkbox id="p-consent" checked={form.consent} onCheckedChange={(v)=>setField("consent", !!v)} data-testid="checkbox-parent-consent"/>
              <label htmlFor="p-consent" className="text-sm text-[#4B6B86] leading-relaxed">
                I consent to School Smiles Australia using this information to respond, in line with the Privacy Policy.
              </label>
            </div>
            {errors.consent && <p className="md:col-span-2 text-sm text-red-600 -mt-2">{errors.consent}</p>}
            <div className="md:col-span-2">
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="submit-parent-form">
                {submitting ? "Sending..." : "Send enquiry"}
                <ArrowRight size={18} strokeWidth={2.5}/>
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

const FieldBlock = ({ label, error, children }) => (
  <div>
    <Label className="text-sm font-medium text-[#14324A]">{label}</Label>
    <div className="mt-2">{children}</div>
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

export default ForParents;
